import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Button, DrawerStepperProgress } from '@mercai/clever'
import { useTranslation } from 'react-i18next'

import {
  useValidationErrors,
  onErrorMessage,
  onSuccessMessage,
} from '@/helpers'

import {
  useAICreateTag,
  useAITagDetail,
  useAIUpdateTag,
  QUERIES_ADMIN,
} from '@/services'

import { IATagProps } from '@/types'

import { schemaTag } from './drawer-form.constants'

import { TagForm } from './components'

import type { DrawerTagFormProps } from './drawer-form.type'

export const TagDrawerForm = ({
  isOpen,
  onHandleOpen,
  tagUuid,
}: DrawerTagFormProps) => {
  const { t } = useTranslation('screenAiCenter')

  const isNewRegister = !tagUuid

  const queryClient = useQueryClient()

  const [data, setData] = useState<Partial<IATagProps>>({})

  const {
    isValid: isValidTag,
    errors: tagErrors,
    onHandleListenErrors: onHandleListenErrorsTag,
  } = useValidationErrors(schemaTag(t), data)

  const { data: dataTag } = useAITagDetail(tagUuid || '')

  const { mutateAsync: mutateAsyncUpdateTag } = useAIUpdateTag()
  const { mutateAsync: mutateAsyncCreateTag } = useAICreateTag()

  const onSubmitAgentForm = async () => {
    if (!isValidTag) {
      onHandleListenErrorsTag(true)
      return
    }

    onHandleListenErrorsTag(false)

    let response: {
      success: boolean
      message: string
      data?: IATagProps
    } = {
      success: false,
      message: '',
      data: {} as IATagProps,
    }

    if (isNewRegister) {
      response = await mutateAsyncCreateTag(data as IATagProps)
    } else {
      response = await mutateAsyncUpdateTag(data as IATagProps)
    }

    if (response.success) {
      queryClient.refetchQueries({
        queryKey: [QUERIES_ADMIN.AI_TAG.module],
      })

      onSuccessMessage(response.message)

      onHandleOpen(false)
    } else {
      if (isNewRegister) {
        onErrorMessage(t('tagFormErrorTitle'), response.message)
      } else {
        onErrorMessage(t('tagFormErrorTitle'), response.message)
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      if (isNewRegister) {
        setData({})
      } else {
        if (dataTag?.data) {
          setData({
            ...dataTag.data,
          })
        }
      }
    }
  }, [isOpen, dataTag])

  return (
    <DrawerStepperProgress
      translations={{
        optional: t('tagFormOptional'),
      }}
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={isNewRegister ? t('tagFormTitleCreate') : t('tagFormTitleUpdate')}
      initialStep={'root'}
      steps={{
        root: {
          isOptional: false,
          backNavigate: 'root',
          stepNumber: 0,
          component: (
            <TagForm
              errors={tagErrors as Record<string, string>}
              values={data || {}}
              onChangeValues={setData}
            />
          ),
          footer: ({ onNavigateToPreviousStep }) => (
            <>
              <Button
                variant="secondary"
                onClick={() => onNavigateToPreviousStep()}
              >
                {t('tagFormNavigateButtonBack')}
              </Button>

              <Button onClick={() => onSubmitAgentForm()}>
                {t('tagFormNavigateButtonSaveAndNext')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
